import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Star, Truck, ShieldCheck, MapPin, Minus, Plus, Heart, Check,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList,
  BreadcrumbPage, BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import ProductCard from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { formatBRL, installmentText, discountPercent } from "../lib/format";
import { getProduct, getProducts } from "../services/woocommerce";

const Stars = ({ value = 4.6 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`h-4 w-4 ${n <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
      />
    ))}
  </div>
);

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  const { addToCart, toggleFavorite, isFavorite } = useCart();
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [cep, setCep] = useState("");
  const [freight, setFreight] = useState(null);

  useEffect(() => {
    setLoading(true);
    getProduct(slug)
      .then(p => {
        setProduct(p);
        setActiveImg(0);
        // Busca produtos relacionados simples
        getProducts({ per_page: 4, category: p.categoryId || '' }).then(setRelated).catch(console.error);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-slate-600">Carregando produto...</div>;
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="text-slate-600">Produto não encontrado.</p>
        <Button asChild className="mt-4 bg-brand hover:bg-brand-hover"><Link to="/loja">Voltar à loja</Link></Button>
      </div>
    );
  }

  const hasDiscount = product.oldPrice && product.oldPrice > product.price;
  const fav = isFavorite(product.id);

  const calcFreight = (e) => {
    e.preventDefault();
    if (cep.length < 8) return toast.error("Informe um CEP válido");
    setFreight({ price: 24.9, days: 3 });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Início</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/loja">Produtos</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbPage className="line-clamp-1">{product.name}</BreadcrumbPage></BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Gallery */}
        <div className="flex flex-col-reverse sm:flex-row gap-4">
          <div className="flex sm:flex-col gap-3">
            {product.gallery.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                data-testid={`gallery-thumb-${i}`}
                className={`h-20 w-20 rounded-md overflow-hidden border-2 transition-colors ${
                  activeImg === i ? "border-brand" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <img src={img} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          <div className="flex-1 bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
            <img
              src={product.gallery[activeImg] || "https://placehold.co/600x600/f8fafc/94a3b8?text=Sem+Foto"}
              alt={product.name}
              className="w-full aspect-square object-cover"
              data-testid="product-main-image"
            />
          </div>
        </div>

        {/* Info */}
        <div>
          <span className="text-xs font-semibold uppercase tracking-wide text-brand">{product.category}</span>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" dangerouslySetInnerHTML={{__html: product.name}}></h1>
          <div className="mt-3 flex items-center gap-4 text-sm text-slate-500">
            <span>Cód: <span className="font-medium text-slate-700">{product.sku}</span></span>
            <span className="flex items-center gap-1.5"><Stars value={product.rating} /> ({product.reviews})</span>
          </div>

          <div className="mt-5 border-t border-b border-slate-200 py-5">
            {hasDiscount && (
              <span className="flex items-center gap-2">
                <span className="text-sm text-slate-400 line-through">{formatBRL(product.oldPrice)}</span>
                <span className="bg-brand text-white text-xs font-bold px-2 py-0.5 rounded">-{discountPercent(product.oldPrice, product.price)}%</span>
              </span>
            )}
            <p className="mt-1 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">{formatBRL(product.price)}</p>
            <p className="mt-1 text-sm text-slate-500">{installmentText(product.price)} sem juros</p>
          </div>

          {/* Freight */}
          <form onSubmit={calcFreight} className="mt-5">
            <label className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-brand" /> Calcule o frete
            </label>
            <div className="mt-2 flex gap-2 max-w-sm">
              <Input
                data-testid="cep-input"
                value={cep}
                onChange={(e) => setCep(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="Digite seu CEP"
                className="h-11 border-slate-300"
              />
              <Button type="submit" variant="outline" className="h-11 border-slate-300" data-testid="calc-freight-button">Calcular</Button>
            </div>
            {freight && (
              <p className="mt-2 text-sm text-slate-600" data-testid="freight-result">
                Entrega padrão: <span className="font-semibold">{formatBRL(freight.price)}</span> — {freight.days} dias úteis
              </p>
            )}
          </form>

          {/* Quantity + actions */}
          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-slate-300 rounded-md h-12">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease" className="h-12 w-11 flex items-center justify-center text-slate-600 hover:bg-slate-50">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} data-testid="qty-increase" className="h-12 w-11 flex items-center justify-center text-slate-600 hover:bg-slate-50">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => toggleFavorite(product.id)}
              data-testid="pdp-favorite-button"
              className="h-12 w-12 rounded-md border border-slate-300 flex items-center justify-center hover:bg-slate-50"
              aria-label="Favoritar"
            >
              <Heart className={`h-5 w-5 ${fav ? "fill-brand text-brand" : "text-slate-500"}`} />
            </button>
          </div>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Button
              data-testid="pdp-add-to-cart"
              onClick={() => { addToCart(product, qty); toast.success("Produto adicionado ao carrinho"); }}
              className="h-12 bg-brand hover:bg-brand-hover text-white font-semibold rounded-md active:scale-[0.98] transition-all"
            >
              Adicionar ao carrinho
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="pdp-buy-now"
              className="h-12 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-semibold rounded-md transition-all"
            >
              <Link to="/carrinho" onClick={() => addToCart(product, qty)}>Comprar agora</Link>
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            {[
              { icon: Check, text: "Produto disponível" },
              { icon: ShieldCheck, text: "Compra segura" },
              { icon: Truck, text: "Entrega para sua região" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2 text-slate-600">
                <Icon className="h-4 w-4 text-brand shrink-0" /> {text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-12">
        <Tabs defaultValue="descricao">
          <TabsList className="w-full sm:w-auto justify-start overflow-x-auto bg-slate-100">
            <TabsTrigger value="descricao" data-testid="tab-descricao">Descrição</TabsTrigger>
            <TabsTrigger value="caracteristicas" data-testid="tab-caracteristicas">Características</TabsTrigger>
            <TabsTrigger value="especificacoes" data-testid="tab-especificacoes">Especificações</TabsTrigger>
            <TabsTrigger value="avaliacoes" data-testid="tab-avaliacoes">Avaliações</TabsTrigger>
          </TabsList>
          <TabsContent value="descricao" className="pt-6 text-slate-600 leading-relaxed max-w-3xl">
            <div dangerouslySetInnerHTML={{ __html: product.short_description || product.description || "Sem descrição." }}></div>
          </TabsContent>
          <TabsContent value="caracteristicas" className="pt-6">
            <ul className="space-y-2 text-slate-600 max-w-2xl">
              {["Alta durabilidade e resistência", "Fácil aplicação", "Ótimo custo-benefício", "Indicado para uso profissional e residencial"].map((c) => (
                <li key={c} className="flex items-start gap-2"><Check className="h-4 w-4 text-brand mt-0.5 shrink-0" /> {c}</li>
              ))}
            </ul>
          </TabsContent>
          <TabsContent value="especificacoes" className="pt-6">
            <div className="max-w-2xl border border-slate-200 rounded-lg overflow-hidden">
              {[
                ["Marca", product.brand],
                ["Categoria", product.category],
                ["Código", product.sku],
                ["Garantia", "3 meses"],
                ["Origem", "Nacional"],
              ].map(([k, v], i) => (
                <div key={k} className={`flex text-sm ${i % 2 ? "bg-white" : "bg-slate-50"}`}>
                  <span className="w-40 px-4 py-3 font-medium text-slate-700">{k}</span>
                  <span className="flex-1 px-4 py-3 text-slate-600">{v}</span>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="avaliacoes" className="pt-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl font-extrabold text-slate-900">{product.rating.toFixed(1)}</span>
              <div><Stars value={product.rating} /><p className="text-xs text-slate-500 mt-1">{product.reviews} avaliações</p></div>
            </div>
            <div className="space-y-4 max-w-2xl">
              {[["Carlos M.", "Ótimo produto, chegou rápido e bem embalado."], ["Ana P.", "Qualidade excelente pelo preço. Recomendo!"]].map(([name, txt]) => (
                <div key={name} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between"><span className="font-semibold text-slate-800 text-sm">{name}</span><Stars value={5} /></div>
                  <p className="mt-2 text-sm text-slate-600">{txt}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="mt-14">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-6">Você também pode gostar</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {related.filter(p => p.id !== product.id).slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
